import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { EditableContent } from "../components/EditableContent";
import { ScorePill } from "../components/Ratings";
import {
  Search, ArrowRight, ShieldCheck, TrendingUp, Handshake, Star,
  Check, MessageCircle, Building2, CheckCircle2, Clock, AlertCircle, Loader2,
} from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/30386991/pexels-photo-30386991.jpeg";
export const FALLBACK = "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export function propPhoto(p) {
  if (p.photo_external) return p.photo_external;
  if (p.has_photo) return `${API}/properties/${p.slug}/photo`;
  return FALLBACK;
}

const STATUS = {
  "Landlord replied": { icon: MessageCircle, cls: "text-mint-dark" },
  "Verified": { icon: CheckCircle2, cls: "text-mint-dark" },
  "Pending response": { icon: Clock, cls: "text-slate-400" },
};

export default function Home() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState({ properties: 0, live_reviews: 0, landlord_responses: 0, issues_resolved: 0, trust_score: 4.8 });
  const [recent, setRecent] = useState([]);
  const [visible, setVisible] = useState(6);
  const [recentLoading, setRecentLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/properties/recent").then((r) => setRecent(r.data)).catch(() => {}).finally(() => setRecentLoading(false));
  }, []);

  const search = async (term) => {
    setQ(term);
    if (!term.trim()) { setResults([]); return; }
    try { const res = await api.get(`/properties/search?q=${encodeURIComponent(term)}`); setResults(res.data); }
    catch { setResults([]); }
  };

  const go = () => { if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`); };

  const fmt = (n) => n.toLocaleString("en-GB");

  return (
    <div className="App bg-[#F4F7FA]">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-navy text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/85 via-navy/90 to-navy" />
        </div>
        <div className="relative z-20 max-w-5xl mx-auto px-5 pt-20 pb-36 text-center animate-fade-up">
          <h1 className="font-heading font-extrabold tracking-tight text-5xl md:text-7xl leading-[1.02]">
            Know <span className="text-mint">BEFORE</span> You Rent
          </h1>
          <p className="mt-5 text-lg md:text-2xl text-slate-300 font-medium">Real tenant reviews.</p>

          <div className="mt-10 relative max-w-3xl mx-auto" data-testid="hero-search-wrap">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-white rounded-2xl p-2 shadow-2xl">
              <div className="flex items-center flex-1 min-w-0 px-4">
                <Search className="w-5 h-5 text-slate-400 shrink-0" />
                <input data-testid="search-address-input" value={q} onChange={(e) => search(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && go()}
                  placeholder="Enter an address, street or postcode..."
                  className="flex-1 min-w-0 h-12 px-3 text-slate-800 outline-none bg-transparent placeholder:text-slate-400" />
              </div>
              <button data-testid="check-property-btn" onClick={go}
                className="flex items-center justify-center gap-2 bg-mint hover:bg-mint-dark text-white font-semibold px-6 h-12 rounded-xl transition-colors whitespace-nowrap w-full sm:w-auto shrink-0">
                Check Property <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            {results.length > 0 && (
              <div className="absolute z-30 left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl overflow-hidden text-left" data-testid="search-results">
                {results.map((p) => (
                  <button key={p.id} data-testid={`search-result-${p.slug}`} onClick={() => navigate(`/address/${p.slug}`)}
                    className="w-full text-left px-5 py-3 hover:bg-slate-50 flex items-center justify-between border-b border-slate-100 last:border-0 transition-colors">
                    <span className="font-medium text-navy">{p.full_address}</span>
                    <span className="font-mono text-sm text-slate-500 shrink-0 ml-3">{p.aggregates.count} review{p.aggregates.count !== 1 ? "s" : ""}</span>
                  </button>
                ))}
              </div>
            )}
            {q.trim().length > 2 && results.length === 0 && (
              <div className="absolute z-30 left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl p-5 text-left" data-testid="search-no-results">
                <p className="font-semibold text-navy">No reviews yet for "{q}".</p>
                <p className="text-sm text-slate-500 mt-1">Live here, or used to? Be the first to leave a verified review.</p>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <button data-testid="search-leave-review" onClick={() => navigate("/write-review")}
                    className="bg-mint text-white font-semibold text-sm px-4 py-2 rounded-lg hover:bg-mint-dark transition-colors">Live here? Leave a review</button>
                  <button data-testid="search-tips" onClick={() => navigate("/renting-tips")}
                    className="border border-slate-300 text-navy font-semibold text-sm px-4 py-2 rounded-lg hover:bg-slate-50 transition-colors">Thinking of renting? See tips</button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 flex items-center justify-center gap-6 md:gap-10 flex-wrap text-sm text-slate-200">
            {["Tenancy-verified reviews", "Property issue history", "Landlord responses"].map((t) => (
              <span key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-mint" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Stats card */}
      <div className="max-w-6xl mx-auto px-5 -mt-20 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-100" data-testid="stats-bar">
          <Stat icon={Building2} value={fmt(stats.properties)} label="Properties Reviewed" />
          <Stat icon={ShieldCheck} value={fmt(stats.live_reviews)} label="Verified Tenancies" />
          <Stat icon={MessageCircle} value={fmt(stats.landlord_responses)} label="Landlord Responses" />
          <Stat icon={CheckCircle2} value={fmt(stats.issues_resolved)} label="Issues Resolved" />
        </div>
      </div>

      {/* Recently reviewed */}
      <section className="max-w-7xl mx-auto px-5 pt-20" id="recent">
        <div className="flex items-end justify-between">
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-navy">Recently Reviewed Properties</h2>
          <span className="hidden sm:flex items-center gap-1 text-mint-dark font-semibold text-sm">View All <ArrowRight className="w-4 h-4" /></span>
        </div>
        <div className="mt-8 grid md:grid-cols-3 gap-6" data-testid="recent-properties">
          {recentLoading && (
            <div className="md:col-span-3 flex justify-center py-10" data-testid="recent-loading">
              <Loader2 className="w-8 h-8 text-mint animate-spin" />
            </div>
          )}
          {!recentLoading && recent.length === 0 && <p className="text-slate-500">No reviewed properties yet — be the first to leave a review.</p>}
          {recent.slice(0, visible).map((p) => {
            const s = STATUS[p.headline] || STATUS["Pending response"];
            return (
              <button key={p.id} data-testid={`recent-${p.slug}`} onClick={() => navigate(`/address/${p.slug}`)}
                className="text-left bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="h-44 overflow-hidden">
                  <img src={propPhoto(p)} alt={p.address_line} onError={(e) => { e.currentTarget.src = FALLBACK; }} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-bold text-lg text-navy leading-tight">{p.address_line}</h3>
                  <p className="text-mint-dark font-medium text-sm">{p.city}</p>
                  {p.subscription_active && (
                    <span className="mt-2 inline-flex items-center gap-1 bg-mint-soft text-mint-dark px-2 py-0.5 rounded-md text-xs font-semibold" data-testid={`recent-verified-${p.slug}`}>
                      <ShieldCheck className="w-3.5 h-3.5" /> Verified Landlord
                    </span>
                  )}
                  <div className="mt-3"><ScorePill value={p.aggregates.overall} /></div>
                  {p.common_issue && (
                    <p className="mt-3 flex items-center gap-1.5 text-sm text-slate-600">
                      <AlertCircle className="w-4 h-4 text-amber-500" /> Common Issue: <span className="font-medium">{p.common_issue}</span>
                    </p>
                  )}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-sm text-slate-500">
                    <span className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4" /> {p.aggregates.count} reviews</span>
                    <span className={`flex items-center gap-1.5 font-medium ${s.cls}`}><s.icon className="w-4 h-4" /> {p.headline}</span>
                  </div>
                </div>
              </button>
            );
          })}
          {recent.length > 0 && visible >= recent.length && (
            <button data-testid="recent-search-card" onClick={() => { document.querySelector('[data-testid="search-address-input"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); document.querySelector('[data-testid="search-address-input"]')?.focus(); }}
              className="text-left bg-gradient-to-br from-navy to-navy-600 rounded-2xl border border-navy overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all flex flex-col items-center justify-center p-8 text-center min-h-[300px]">
              <Search className="w-9 h-9 text-mint" />
              <h3 className="mt-4 font-heading font-bold text-xl text-white">Looking for a specific place?</h3>
              <p className="mt-2 text-sm text-slate-300">Use the search bar to find any property or town across the UK.</p>
              <span className="mt-4 inline-flex items-center gap-1.5 bg-mint text-white font-semibold px-4 py-2 rounded-xl text-sm">Search properties <ArrowRight className="w-4 h-4" /></span>
            </button>
          )}
        </div>
        {recent.length > visible && (
          <div className="mt-8 flex justify-center">
            <button data-testid="recent-show-more" onClick={() => setVisible((v) => v + 6)}
              className="inline-flex items-center gap-2 border-2 border-navy text-navy font-semibold px-6 py-3 rounded-xl hover:bg-navy hover:text-white transition-colors">
              Show more properties <ArrowRight className="w-4 h-4 rotate-90" />
            </button>
          </div>
        )}
      </section>

      {/* Advertisement slot — edit public_html/content/ad.html in cPanel to change/remove */}
      <section className="max-w-3xl mx-auto px-5 pt-10" data-testid="home-ad-slot">
        <EditableContent file="ad.html" executeScripts className="w-full text-center overflow-hidden empty:hidden" />
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 pt-20" id="how">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, t: "Trusted Reviews", d: "All reviews are verified with proof of tenancy before they're published." },
            { icon: TrendingUp, t: "Property Insights", d: "See patterns, recurring issues and repair history for any address." },
            { icon: Handshake, t: "Landlord Responses", d: "See how responsible landlords resolve problems with proof of work." },
          ].map((c) => (
            <div key={c.t} className="bg-navy text-white rounded-2xl p-8 text-center">
              <div className="w-14 h-14 mx-auto bg-mint/20 rounded-full flex items-center justify-center">
                <c.icon className="w-7 h-7 text-mint" />
              </div>
              <h3 className="mt-5 font-heading font-bold text-xl">{c.t}</h3>
              <p className="mt-2 text-slate-300 text-sm leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 py-20" id="about">
        <div className="rounded-2xl p-10 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6 bg-gradient-to-r from-navy to-[#16584a]">
          <div className="flex items-center gap-5">
            <MessageCircle className="w-12 h-12 text-mint shrink-0" />
            <div>
              <h3 className="font-heading font-extrabold text-2xl md:text-3xl text-white">Have you rented? Share your experience.</h3>
              <p className="mt-1 text-slate-300">Help future tenants make informed decisions.</p>
            </div>
          </div>
          <button data-testid="cta-write-review" onClick={() => navigate("/write-review")}
            className="flex items-center gap-2 bg-mint hover:bg-mint-light text-navy font-semibold px-7 py-3.5 rounded-xl transition-colors whitespace-nowrap">
            Leave a Review <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <footer className="mt-4">
        <Footer />
      </footer>
    </div>
  );
}

const Stat = ({ icon: Icon, value, label, amber }) => (
  <div className="flex flex-col items-center justify-center py-7 px-4 gap-1">
    <div className="flex items-center gap-2">
      <Icon className={`w-6 h-6 ${amber ? "text-amber-400 fill-amber-400" : "text-mint-dark"}`} />
      <span className="font-heading font-extrabold text-3xl text-navy">{value}</span>
    </div>
    <span className="text-sm text-slate-500">{label}</span>
  </div>
);
